import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { css } from '@emotion/react';
import t from 'react-translate';
import { NvPillTab, BadgeColor } from 'shared/components/nv-pill-tab';
import { dropdownZIndex, halfSpacing, standardSpacing } from 'styles/global_defaults/scaffolding';
import { teal } from 'styles/global_defaults/colors';
import { getLearnerProgressCounts } from 'redux/actions/learner-progress';
import { LearnerProgressSavedView } from 'redux/schemas/models/learner-progress-saved-view';
import { RootState, CombinedCourse } from 'redux/schemas';
import { getCurrentCourse, getCourseAliases } from 'redux/selectors/course';
import { Course } from 'redux/schemas/models/course';
import { useAppDispatch } from 'redux/store';
import ClickableContainer from 'components/clickable-container';
import { config } from '../../../config/pendo.config.json';
import SavedViewPill from './saved-view-pill';


export enum TabTypes {
  TOTAL = 'total',
  COMPLETED = 'completed',
  IN_PROGRESS = 'in_progress',
  MISSED_DEADLINE = 'missed_deadline',
  NEVER_ACTIVE = 'never_active',
}
export type LearnerProgressTotals = {
  totalLearners: number,
  completed: number,
  inProgress: number,
  missedDeadlines: number,
  neverActive: number,
};
type LearnerProgressTabsProps = {
  selectTab: (tab: TabTypes) => void,
  selectSavedView: (viewCode: string) => void,
  onEditSavedViewFilters: (view: LearnerProgressSavedView) => void,
  totals: LearnerProgressTotals
};

const SCROLL_AMOUNT = 200;
const ARROW_SIZE = 36;

const LearnerProgressTabs = (props: LearnerProgressTabsProps) => {
  const carouselStyles = css`
    display: flex;
    align-items: center;
    position: relative;
    z-index: ${dropdownZIndex};
    overflow: hidden;
  `;

  const scrollContainerStyles = css`
    overflow-x: auto;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
    flex: 1;
    min-width: 0;
  `;

  const styles = css`
    display: flex;
    gap: ${halfSpacing}px;
    padding: ${halfSpacing}px ${standardSpacing}px;
    align-items: center;
    flex-wrap: nowrap;

    .hover-content {
      padding: ${halfSpacing}px;
    }
  `;

  const scrollArrowStyles = css`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 5;
    width: ${ARROW_SIZE}px;
    height: 100%;
    background-color: ${teal}E6;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    justify-content: center;
    align-items: center;

    &.scroll-arrow-left {
      left: 0;
    }

    &.scroll-arrow-right {
      right: 0;
    }
  `;

  const dispatch = useAppDispatch();
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const checkOverflow = useCallback(() => {
    const el = tabsContainerRef.current;
    if (!el) return;
    setShowLeftArrow(el.scrollLeft > 0);
    setShowRightArrow(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  const scrollLeft = () => tabsContainerRef.current?.scrollBy({ left: -SCROLL_AMOUNT, behavior: 'smooth' });
  const scrollRight = () => tabsContainerRef.current?.scrollBy({ left: SCROLL_AMOUNT, behavior: 'smooth' });

  const aliases = useSelector((state) => getCourseAliases(state));

  const {
    totalLearners,
    completed,
    inProgress,
    missedDeadlines,
    neverActive,
  } = props.totals;

  const [activeTab, setActiveTab] = useState<string>(TabTypes.TOTAL);
  const currentCourse = useSelector<RootState, CombinedCourse>((state) => getCurrentCourse(state)) as Course;
  const savedViews: LearnerProgressSavedView[] = useSelector((state: RootState) => state.app.learnerProgressDashboard.savedViews);

  const tabs = [
    {
      text: t.LEARNER_PROGRESS.TABS.TOTAL_LEARNERS({ ...aliases.learnersAliases }),
      count: totalLearners,
      onClick: () => {
        setActiveTab(TabTypes.TOTAL);
        props.selectTab(TabTypes.TOTAL);
      },
      id: TabTypes.TOTAL,
      dataQA: config.pendo.learnerProgress.tabs.totalLearers,
      badgeColor: BadgeColor.PRIMARY,
    },
    {
      text: t.LEARNER_PROGRESS.TABS.COMPLETED(),
      count: completed,
      onClick: () => {
        setActiveTab(TabTypes.COMPLETED);
        props.selectTab(TabTypes.COMPLETED);
      },
      id: TabTypes.COMPLETED,
      dataQA: config.pendo.learnerProgress.tabs.completed,
      badgeColor: BadgeColor.SUCCESS,
    },
    {
      text: t.LEARNER_PROGRESS.TABS.IN_PROGRESS(),
      count: inProgress,
      onClick: () => {
        setActiveTab(TabTypes.IN_PROGRESS);
        props.selectTab(TabTypes.IN_PROGRESS);
      },
      id: TabTypes.IN_PROGRESS,
      dataQA: config.pendo.learnerProgress.tabs.inProgress,
      badgeColor: BadgeColor.PRIMARY,
    },
    {
      text: t.LEARNER_PROGRESS.TABS.MISSED_DEADLINES(),
      count: missedDeadlines,
      onClick: () => {
        setActiveTab(TabTypes.MISSED_DEADLINE);
        props.selectTab(TabTypes.MISSED_DEADLINE);
      },
      id: TabTypes.MISSED_DEADLINE,
      dataQA: config.pendo.learnerProgress.tabs.missedDeadlines,
      badgeColor: BadgeColor.DANGER,
    },
    {
      text: t.LEARNER_PROGRESS.TABS.NEVER_ACTIVE(),
      count: neverActive,
      onClick: () => {
        setActiveTab(TabTypes.NEVER_ACTIVE);
        props.selectTab(TabTypes.NEVER_ACTIVE);
      },
      id: TabTypes.NEVER_ACTIVE,
      dataQA: config.pendo.learnerProgress.tabs.neverActive,
      badgeColor: BadgeColor.DANGER,
    },
  ];

  useEffect(() => {
    dispatch(getLearnerProgressCounts({
      catalogId: currentCourse.catalogId,
    }));
  }, []);

  useEffect(() => {
    checkOverflow();
    const el = tabsContainerRef.current;
    el?.addEventListener('scroll', checkOverflow);
    window.addEventListener('resize', checkOverflow);
    return () => {
      el?.removeEventListener('scroll', checkOverflow);
      window.removeEventListener('resize', checkOverflow);
    };
  }, [tabs.length, checkOverflow]);

  return (
    <div css={carouselStyles}>
      {showLeftArrow && (
        <ClickableContainer
          onClick={scrollLeft}
          className='scroll-arrow scroll-arrow-left'
          css={scrollArrowStyles}
          aria-label={t.LEARNER_PROGRESS.TABS.SCROLL_LEFT()}
        >
          <i className='icon icon-xs-small icon-arrow-left' />
        </ClickableContainer>
      )}
      <div css={scrollContainerStyles} ref={tabsContainerRef}>
        <div css={styles}>
          {tabs.map((tab) => (
            <NvPillTab
              key={tab.id}
              label={tab.text}
              count={tab.count}
              isSelected={activeTab === tab.id}
              onClick={tab.onClick}
              id={tab.id}
              dataQA={tab.dataQA}
              badgeColor={tab.badgeColor}
            />
          ))}
          {savedViews.map((view) => {
            const tabId = `saved_${view.viewCode}`;
            return (
              <SavedViewPill
                key={tabId}
                view={view}
                catalogId={currentCourse.catalogId}
                isSelected={activeTab === tabId}
                onClick={() => {
                  setActiveTab(tabId);
                  props.selectSavedView(view.viewCode);
                }}
                onViewUpdated={(updatedView) => {
                  const newTabId = `saved_${updatedView.viewCode}`;
                  setActiveTab(newTabId);
                  props.selectSavedView(updatedView.viewCode);
                }}
                onEditFilters={() => props.onEditSavedViewFilters(view)}
                tabId={tabId}
              />
            );
          })}
        </div>
      </div>
      {showRightArrow && (
        <ClickableContainer
          onClick={scrollRight}
          className='scroll-arrow scroll-arrow-right'
          css={scrollArrowStyles}
          aria-label={t.LEARNER_PROGRESS.TABS.SCROLL_RIGHT()}
        >
          <i className='icon icon-xs-small icon-arrow-right' />
        </ClickableContainer>
      )}
    </div>
  );
};

export default LearnerProgressTabs;
